// src/views/Dashboard.jsx
// Extracted from App.jsx — behavior-preserving split (2026-07-20).

import React, { useState, useMemo, useEffect, useRef } from "react";
import { C, Ic } from '../theme';
import { Badge, Btn, CallBtn, Hdr, Inp, Sel, Stat } from '../ui';
import { CONTACT_LOG_MAX, MANUAL_DEAL_LABEL, STAGES, STC, URGENT_ACK_KEY } from '../constants';
import { collectFollowUpItems, FOLLOWUP_HORIZON_DAYS, contactEntry, leadMetaFrom, leadMetaSet, exportCSV, isoDatePlus, parseDateLocal, prospectFromLead, prospectFromLoan, readUrgentAck, ownerOf, leadOwner, stampLeadOwner } from '../lib';
import * as BorrowerAPI from '../borrowerApi';
import { supabase, isSupabaseReady, STAFF } from '../supabase';
import { staffAccess } from '../access';
import { WhiteboardPanel } from './Whiteboard';
import { CallbacksPanel } from './Callbacks';
import { InterestMenu } from './Interest';
import { applyInterest, interestNameStyle, interestStyle } from '../interest';
import { fmtDay, fmtMoney, owedByFile } from '../reimbursements';
import { useOwnershipUi } from '../features';

// ═══════ OWNER CHIP ═══════
// R / M initial in the staff colour (same as the top-bar "Signed in as"
// avatar). Owner is a tracking tag — both staff see everything. Shared by
// Pipeline, Fallout, Re-price Radar, Loan Detail and Inquiry. Renders nothing
// for an unknown key (an unowned LEAD has no owner to show) — and nothing at
// all while "Lead assignment & Max on files" is off (src/features.js, 10/10:
// the default), wherever it is used.
function OwnerChip({owner,size,title,onClick,label}){
  const ownershipUi=useOwnershipUi();
  const s=STAFF[owner];if(!s||!ownershipUi)return null;
  const sz=size||18;
  const dot=<span style={{width:sz,height:sz,borderRadius:"50%",background:owner==="max"?C.clay:C.navy,color:"#fff",fontSize:Math.max(9,Math.round(sz*0.52)),fontWeight:800,display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0,lineHeight:1,fontFamily:"'Mulish',system-ui,sans-serif"}}>{s.short}</span>;
  if(!label)return <span onClick={onClick} title={title||("Owner: "+s.name)} style={{display:"inline-flex",cursor:onClick?"pointer":"default",flexShrink:0}}>{dot}</span>;
  return <span onClick={onClick} title={title||("Owner: "+s.name)} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"2px 9px 2px 2px",borderRadius:20,border:"1px solid "+C.border,fontSize:11,fontWeight:700,color:C.textMuted,whiteSpace:"nowrap",cursor:onClick?"pointer":"default",flexShrink:0,fontFamily:"'Mulish',system-ui,sans-serif"}}>{dot}{label}</span>;
}
// The owner to show for a mail-list lead: its own owner tag, else the file it
// became (that file's owner), else nobody.
function leadOwnerShown(lead,loanById){
  const o=leadOwner(lead);if(o)return o;
  const f=lead&&lead.converted_loan_id&&loanById?loanById.get(lead.converted_loan_id):null;
  return f?ownerOf(f):null;
}
// "Whoever works it owns it": claim an UNOWNED file for the staff member who
// just worked it. Never overwrites an existing owner.
function claimLoan(l,staff){return(l&&!l.owner&&STAFF[staff])?Object.assign({},l,{owner:staff}):l;}

// ═══════ WEB LEADS ═══════
// The inbox for every lead that came in online — statement uploads, web-form
// and callback requests (marketing_leads tagged inbound-statement /
// inbound-callback / web, or list_source "Statement Submission") plus
// statements submitted through the borrower portal (loans carrying
// statementData). Three tabs:
//   New     — nobody has touched it yet
//   Working — has notes or a follow-up, no outcome yet
//   Done    — has an outcome (pitched / fallout / DNC) or was marked done
// Quick notes are logged right on the card (timestamped, same history New
// Call reads). "Work it" opens the full New Call card for pricing, follow-up
// and the outcome buttons. Nothing ever drops off the list.
const WEB_TAGS=["inbound-statement","inbound-callback","inbound-inquiry","web"];
const isWebLead=function(l){return (l.tags||[]).some(function(t){return WEB_TAGS.indexOf(t)>=0;})||l.list_source==="Statement Submission";};
// Homepage "See what's available" inquiries (api/intake mode "inquiry") carry
// inbound-inquiry plus inquiry-hs2 / inquiry-ftr. null = not an inquiry.
const inquiryLabel=function(tags){
  const t=tags||[];
  if(t.indexOf("inquiry-hs2")>=0)return"HomeSafe Second inquiry";
  if(t.indexOf("inquiry-ftr")>=0)return"First-Time Reverse inquiry";
  return t.indexOf("inbound-inquiry")>=0?"Web inquiry":null;
};
function WebLeads({mktgLeads,setMktgLeads,loans,setLoans,setNav,setSel,onStartCall,staff,registerFlush}){
  const me=staff||"reece";
  const ownershipUi=useOwnershipUi();
  const [tab,sTab]=useState("new");
  const [openErr,sOpenErr]=useState("");
  // kind+"-"+id → quick-note text. Notes save without a click: a non-empty
  // draft is committed as ONE entry on Enter, on blur, and when the dashboard
  // unmounts (navigate away) — see the cleanup effect below.
  const [drafts,sDrafts]=useState({});
  const [savedKey,sSavedKey]=useState(null);
  const draftsRef=useRef({});draftsRef.current=drafts;

  // Structured "Key: value" lines the intake writes into lead notes.
  const parseNotes=function(notes){
    const out=[];
    String(notes||"").split(/\n/).forEach(function(line){
      const m=/^(Current servicer|Property value \(their estimate\)|Balance|Credit|Date of birth|Amount they're looking for|Rate|Principal limit|Available LOC|Youngest DOB|Military service|Annual tax|Annual insurance|Goals)\s*:\s*(.+)$/i.exec(line.trim());
      if(m)out.push([m[1],m[2]]);
    });
    return out;
  };
  const photoPathOf=function(tags){
    const t=(tags||[]).find(function(x){return typeof x==="string"&&x.indexOf("photo:")===0;});
    return t?t.slice(6):null;
  };
  const loanById=new Map((loans||[]).map(function(l){return [l.id,l];}));

  // ── Collect both sources into one list ──
  const items=[];
  (mktgLeads||[]).forEach(function(l){
    if(!isWebLead(l))return;
    const hist=(Array.isArray(l.history)?l.history:[]).filter(function(h){return h&&h.type!=="profile";});
    const notes=hist.filter(function(h){return h.type==="note"||h.type==="call"||h.type==="mail";});
    const dispo=hist.filter(function(h){return h.type==="disposition"||h.type==="pitch";}).slice(-1)[0];
    const file=l.converted_loan_id?loanById.get(l.converted_loan_id):null;
    let outcome="";
    if(file)outcome=file.stage+(file.falloutReason?" — "+file.falloutReason:"");
    else if(dispo)outcome=dispo.text||"";
    else if(["DNC","Dead","Opt-Out Requested"].indexOf(l.status)>=0||l.doNotContact)outcome="Do not contact";
    const kind=l.list_source==="Statement Submission"||(l.tags||[]).indexOf("inbound-statement")>=0?"Statement":(l.tags||[]).indexOf("inbound-callback")>=0?"Callback request":inquiryLabel(l.tags)||"Web form";
    items.push({
      kind:"lead",id:l.id,label:kind,name:l.name||"(no name)",phone:l.phone||"",email:l.email||"",
      address:[l.address,l.city,l.state].filter(Boolean).join(", "),
      epoch:l.created_at?Date.parse(l.created_at)||0:0,
      when:l.created_at?new Date(l.created_at).toLocaleString():"",
      figures:parseNotes(l.notes),photoPath:photoPathOf(l.tags),
      notes:notes,outcome:outcome,followUp:l.follow_up_date||"",
      markedDone:(l.tags||[]).indexOf("stmt-reviewed")>=0,
      src:l,
    });
  });
  (loans||[]).forEach(function(l){
    if(l.archived||!l.statementData)return;
    const sd=l.statementData;
    const figures=[["Servicer",sd.servicer],["Balance",sd.balance],["Rate",sd.rate],["Principal limit",sd.principalLimit],["Available LOC",sd.loc],["Goals",sd.goals]].filter(function(r){return r[1];});
    const photoUpload=(l.uploads||[]).find(function(u){return u&&u.storagePath&&(u.note||"").indexOf("statement submission")>=0;});
    const notes=(l.contactLog||[]).filter(function(e){return e&&(e.type==="note"||e.type==="call");});
    items.push({
      kind:"loan",id:l.id,label:"Portal statement",name:l.b||"(no name)",phone:l.ph||"",email:l.em||"",address:l.prop||"",
      epoch:sd.submittedAt?Date.parse(sd.submittedAt)||0:0,when:sd.submittedDate||"",
      figures:figures,photoPath:photoUpload?photoUpload.storagePath:null,
      notes:notes,outcome:l.stage&&l.stage!=="Prospect"?l.stage:"",followUp:l.followUpDate||"",
      markedDone:!!(l.deal&&l.deal.stmtReviewed),
      src:l,
    });
  });
  items.forEach(function(x){x.bucket=(x.outcome||x.markedDone)?"done":(x.notes.length||x.followUp)?"working":"new";});
  items.sort(function(a,b){return b.epoch-a.epoch;});
  const counts={new:0,working:0,done:0};items.forEach(function(x){counts[x.bucket]++;});
  const shown=items.filter(function(x){return x.bucket===tab;});

  // ── Actions ──
  const openPhoto=async function(path){
    if(!path)return;
    if(!isSupabaseReady()){sOpenErr("Storage isn't connected in this environment.");return;}
    sOpenErr("");
    const w=window.open("","_blank"); // open synchronously so popup blockers allow it
    try{
      const {data}=await supabase.auth.getSession();
      const token=data&&data.session&&data.session.access_token;
      const r=await BorrowerAPI.adminGetDocUrl({accessToken:token,path:path});
      if(r&&r.ok&&r.url){if(w)w.location=r.url;else window.open(r.url,"_blank");}
      else{if(w)w.close();sOpenErr("Couldn't open the statement — please try again.");}
    }catch(e){if(w)w.close();sOpenErr("Couldn't open the statement — please try again.");}
  };
  // "Done" without an outcome — e.g. a duplicate or a test submission.
  const setDone=function(item,val){
    if(item.kind==="lead"&&setMktgLeads){
      setMktgLeads(function(prev){return prev.map(function(x){
        if(x.id!==item.id)return x;
        const tags=(x.tags||[]).filter(function(t){return t!=="stmt-reviewed";});
        return Object.assign({},x,{tags:val?tags.concat(["stmt-reviewed"]):tags});
      });});
    }else if(item.kind==="loan"&&setLoans){
      setLoans(function(prev){return prev.map(function(x){
        if(x.id!==item.id)return x;
        const deal=Object.assign({},x.deal||{});
        if(val)deal.stmtReviewed=true;else delete deal.stmtReviewed;
        return Object.assign({},x,{deal:deal});
      });});
    }
  };
  // Timestamped note into the same history New Call shows. Logging a note on
  // an unowned lead/file claims it for whoever wrote it.
  const writeNote=function(kind,id,text){
    const e=contactEntry("note",text);
    if(kind==="lead"&&setMktgLeads){
      setMktgLeads(function(prev){return prev.map(function(x){
        if(x.id!==id)return x;
        const hist=(Array.isArray(x.history)?x.history:[]).filter(function(h){return !(h&&h.type==="profile");}).concat([e]).slice(-CONTACT_LOG_MAX);
        return stampLeadOwner(Object.assign({},x,{history:leadMetaSet(hist,Object.assign({},leadMetaFrom(x.history),{lastContactAt:e.at})),updated_at:e.at}),me);
      });});
    }else if(kind==="loan"&&setLoans){
      setLoans(function(prev){return prev.map(function(x){
        if(x.id!==id)return x;
        return claimLoan(Object.assign({},x,{
          contactLog:(x.contactLog||[]).concat([e]).slice(-CONTACT_LOG_MAX),
          activityLog:(x.activityLog||[]).concat([{text:text,type:"note",timestamp:e.date+" "+e.time,date:e.date,time:e.time,epoch:e.epoch}]),
          lastContactAt:e.at,
        }),me);
      });});
    }
  };
  const addNote=function(item){
    const key=item.kind+"-"+item.id;
    const text=String(draftsRef.current[key]||"").trim();
    if(!text)return;
    // Clear the ref first so a blur that follows an Enter can't commit twice.
    const nextDrafts=Object.assign({},draftsRef.current);delete nextDrafts[key];
    draftsRef.current=nextDrafts;
    writeNote(item.kind,item.id,text);
    sDrafts(function(d){const n=Object.assign({},d);delete n[key];return n;});
    sSavedKey(key);
  };
  // Navigate away with a half-typed note → it is saved, not dropped.
  const writeNoteRef=useRef(writeNote);writeNoteRef.current=writeNote;
  const flushAllDrafts=function(){
    const d=draftsRef.current||{};
    draftsRef.current={};
    let n=0;
    Object.keys(d).forEach(function(key){
      const text=String(d[key]||"").trim();if(!text)return;
      const i=key.indexOf("-");if(i<0)return;
      writeNoteRef.current(key.slice(0,i),key.slice(i+1),text);n++;
    });
    return n;
  };
  const flushAllRef=useRef(flushAllDrafts);flushAllRef.current=flushAllDrafts;
  useEffect(function(){
    return function(){flushAllRef.current();};
  },[]);
  // Logout / the 30-minute idle sign-out run this BEFORE the session ends, so
  // a quick note typed and left in the box is written while it still can be.
  useEffect(function(){
    if(!registerFlush)return;
    registerFlush("dashboard",function(){if(flushAllRef.current()>0)sDrafts({});});
    return function(){registerFlush("dashboard",null);};
  },[registerFlush]);
  const workIt=function(item){
    if(onStartCall)onStartCall({prospect:item.kind==="loan"?prospectFromLoan(item.src):prospectFromLead(item.src)});
    else if(item.kind==="loan"){if(setSel)setSel(item.src);if(setNav)setNav("loanDetail");}
  };

  const card=function(item){
    const missing=[];
    if(!item.phone)missing.push("phone number");
    if(item.kind==="lead"&&!item.address)missing.push("property address");
    const last=item.notes.slice(-1)[0];
    const stripe=item.bucket==="new"?C.clay:item.bucket==="working"?C.orange:C.green;
    const key=item.kind+"-"+item.id;
    const owner=item.kind==="loan"?ownerOf(item.src):leadOwnerShown(item.src,loanById);
    // Do-not-contact keeps a lead off mailings and the Outbound dialer only
    // (Reece, 10/1) — a web lead can still be called back; the flag is shown
    // for information.
    const dnc=item.kind==="lead"&&(item.src.doNotContact||item.src.do_not_contact);
    return(<div key={key} style={{background:C.card,border:"1px solid "+C.border,borderLeft:"4px solid "+stripe,borderRadius:10,padding:"14px 16px"}}>
      <div style={{display:"flex",alignItems:"baseline",gap:10,flexWrap:"wrap"}}>
        {ownershipUi&&owner&&<span style={{alignSelf:"center",display:"inline-flex"}}><OwnerChip owner={owner}/></span>}
        <span style={{fontSize:14.5,fontWeight:800,color:C.text}}>{item.name}</span>
        <span style={{fontSize:10,fontWeight:800,letterSpacing:.5,textTransform:"uppercase",color:C.accentLight,background:C.accentGlow,borderRadius:20,padding:"2px 8px"}}>{item.label}</span>
        <span style={{fontSize:11,color:C.textDim,marginLeft:"auto"}}>{item.when}</span>
      </div>
      <div style={{display:"flex",gap:14,flexWrap:"wrap",marginTop:5,fontSize:12,color:C.textMuted}}>
        {item.phone?<CallBtn phone={item.phone} small/>:null}
        {dnc&&<span title="Off mailings and the Outbound dialer — calling them back is fine" style={{color:C.textDim}}>Opted out · off mailings</span>}
        {item.email&&<span>✉ {item.email}</span>}
        {item.address&&<span>📍 {item.address}</span>}
      </div>
      {item.figures.length>0&&<div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:8}}>
        {item.figures.slice(0,6).map(function(f){return <span key={f[0]} style={{fontSize:11,fontWeight:600,color:C.text,background:C.surfaceAlt,border:"1px solid "+C.border,borderRadius:6,padding:"3px 8px"}}>{f[0]}: {f[1]}</span>;})}
      </div>}
      {missing.length>0&&item.bucket!=="done"&&<div style={{fontSize:11,color:C.red,marginTop:6,fontWeight:700}}>⚠ Missing: {missing.join(" + ")} — get this on the first contact.</div>}
      {item.label==="Statement"&&!item.photoPath&&item.bucket!=="done"&&<div style={{fontSize:11,color:C.orange,marginTop:6,fontWeight:600}}>No statement file attached — typed figures only.</div>}

      {/* What happened */}
      {(item.outcome||last||item.followUp)&&<div style={{marginTop:10,padding:"8px 12px",borderRadius:8,background:C.surfaceAlt,fontSize:12,color:C.text,display:"flex",flexDirection:"column",gap:4}}>
        {item.outcome&&<div><strong style={{color:C.green}}>Outcome:</strong> {item.outcome}</div>}
        {item.followUp&&!item.outcome&&<div style={{color:C.orange,fontWeight:600}}>Follow up {String(item.followUp).slice(0,10)}</div>}
        {last&&<div><span style={{fontSize:10,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.5,marginRight:6}}>Last note · {last.date} {last.time}</span>{last.text}{item.notes.length>1?<span style={{color:C.textDim}}> ({item.notes.length} notes)</span>:null}</div>}
      </div>}

      {/* Quick note + actions */}
      <div style={{display:"flex",gap:8,marginTop:10,flexWrap:"wrap",alignItems:"center"}}>
        <input value={drafts[key]||""} onChange={function(e){const v=e.target.value;if(savedKey===key)sSavedKey(null);sDrafts(function(d){return Object.assign({},d,{[key]:v});});}}
          onKeyDown={function(e){if(e.key==="Enter")addNote(item);}} onBlur={function(){addNote(item);}} placeholder="Quick note — what happened? (saves automatically)"
          style={{flex:"1 1 240px",minWidth:0,padding:"8px 12px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:12.5,fontFamily:"'Mulish',system-ui,sans-serif",outline:"none"}}/>
        {savedKey===key&&!drafts[key]&&<span style={{fontSize:11,color:C.green,fontWeight:700}}>✓ Saved</span>}
        <Btn small onClick={function(){addNote(item);}} disabled={!String(drafts[key]||"").trim()}>Save note</Btn>
        <Btn small primary onClick={function(){workIt(item);}}>{Ic.phone} Work it / outcome</Btn>
        {item.photoPath&&<Btn small onClick={function(){openPhoto(item.photoPath);}}>📷 Statement</Btn>}
        {item.bucket!=="done"
          ?<Btn small onClick={function(){setDone(item,true);}} style={{color:C.textDim}}>Move to Done</Btn>
          :(item.markedDone&&!item.outcome?<Btn small onClick={function(){setDone(item,false);}} style={{color:C.textDim}}>↩ Reopen</Btn>:null)}
      </div>
    </div>);
  };

  const tabs=[{k:"new",l:"New"},{k:"working",l:"Working"},{k:"done",l:"Done"}];
  return(<div style={{background:"linear-gradient(135deg,"+C.navyDeep+","+C.navy+")",borderRadius:14,padding:"18px 20px",marginBottom:24,border:"2px solid "+C.clay}}>
    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14,flexWrap:"wrap"}}>
      <span style={{fontSize:22}}>🌐</span>
      <div style={{flex:1,minWidth:200}}>
        <div style={{fontSize:15,fontWeight:800,color:C.cream,letterSpacing:.3}}>Web Leads</div>
        <div style={{fontSize:11.5,color:C.cream,opacity:.75}}>Every lead that came in online — statements, web forms, callback requests and the borrower portal.</div>
      </div>
      <div style={{display:"flex",gap:6}}>
        {tabs.map(function(t){const on=tab===t.k;return <button key={t.k} onClick={function(){sTab(t.k);}} style={{border:"1px solid "+(on?C.clay:C.cream+"55"),background:on?C.clay:"transparent",color:on?"#fff":C.cream,fontSize:12,fontWeight:800,borderRadius:20,padding:"6px 14px",cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif"}}>{t.l} ({counts[t.k]})</button>;})}
      </div>
    </div>
    {openErr&&<div style={{fontSize:12,color:"#fff",background:C.red,borderRadius:8,padding:"8px 12px",marginBottom:10,fontWeight:600}}>{openErr}</div>}
    {shown.length===0&&<div style={{fontSize:12.5,color:C.cream,opacity:.75,padding:"4px 2px"}}>
      {tab==="new"?(items.length?"✓ Nothing new — every web lead has been touched.":"No web leads yet — new ones appear here the moment they arrive."):tab==="working"?"Nothing in progress.":"Nothing finished yet."}
    </div>}
    <div style={{display:"flex",flexDirection:"column",gap:8}}>{shown.map(card)}</div>
  </div>);
}
function UrgentAlertBanner({loans,mktgLeads,setNav,setSel,setMktgLeadFocus}){
  const [ackAt,sAckAt]=useState(readUrgentAck);
  const [expanded,sExpanded]=useState(true);
  // Recompute every render — derived state is cheap and always-fresh.
  const callbacks=[];const uploads=[];const statements=[];const portalOther=[];
  (loans||[]).forEach(function(l){
    if(l.archived)return;
    (l.activityLog||[]).forEach(function(e){
      if(!e||!e.epoch||e.epoch<=ackAt)return;
      if(e.kind==="callback")callbacks.push({loan:l,entry:e});
      else if(e.kind==="upload")uploads.push({loan:l,entry:e});
      else if(e.kind==="statement")statements.push({loan:l,entry:e});
      // Borrower changed phone/email (or asked for a name/address change) or
      // confirmed counseling in the portal.
      else if(e.kind==="contact-change"||e.kind==="counseling")portalOther.push({loan:l,entry:e});
    });
  });
  const newLeads=(mktgLeads||[]).filter(function(m){
    const tags=m.tags||[];
    if(tags.indexOf("inbound-callback")<0&&tags.indexOf("inbound-statement")<0&&tags.indexOf("inbound-inquiry")<0)return false;
    const cr=m.created_at?Date.parse(m.created_at):0;
    return cr>ackAt;
  });
  // Opt-out (do-not-mail) requests are COMPLIANCE-sensitive: surface every
  // UNRESOLVED one (status still "Opt-Out Requested") regardless of the
  // per-device "mark seen" ack. The alert must persist until the request is
  // actually processed — i.e. the admin opens it and changes its status — so a
  // do-not-mail request can never be silently dismissed and left unhandled.
  const optouts=(mktgLeads||[]).filter(function(m){
    return (m.tags||[]).indexOf("optout-request")>=0 && m.status==="Opt-Out Requested";
  });
  const total=callbacks.length+uploads.length+statements.length+portalOther.length+newLeads.length+optouts.length;
  if(total===0)return null;

  const markSeen=function(){
    const now=Date.now();
    try{localStorage.setItem(URGENT_ACK_KEY,String(now));}catch(e){}
    sAckAt(now);sExpanded(false);
  };
  const jump=function(loan){if(loan&&setSel&&setNav){setSel(loan);setNav("loanDetail");sExpanded(false);}};
  const jumpLead=function(lead){
    if(setMktgLeadFocus)setMktgLeadFocus(lead);
    if(setNav)setNav("marketing");
    sExpanded(false);
  };

  const summaryBits=[];
  if(callbacks.length+newLeads.filter(function(l){return(l.tags||[]).indexOf("inbound-callback")>=0;}).length>0){
    const n=callbacks.length+newLeads.filter(function(l){return(l.tags||[]).indexOf("inbound-callback")>=0;}).length;
    summaryBits.push(n+" new callback request"+(n>1?"s":""));
  }
  if(uploads.length>0)summaryBits.push(uploads.length+" new upload"+(uploads.length>1?"s":""));
  const stmtCount=statements.length+newLeads.filter(function(l){return(l.tags||[]).indexOf("inbound-statement")>=0;}).length;
  if(stmtCount>0)summaryBits.push(stmtCount+" new statement submission"+(stmtCount>1?"s":""));
  const ccCount=portalOther.filter(function(x){return x.entry.kind==="contact-change";}).length;
  if(ccCount>0)summaryBits.push(ccCount+" contact info change"+(ccCount>1?"s":""));
  const coCount=portalOther.length-ccCount;
  if(coCount>0)summaryBits.push(coCount+" counseling confirmation"+(coCount>1?"s":""));
  // Web inquiries (homepage form) — counted only when not also a callback or
  // statement, so one lead is never two bits.
  const inqCount=newLeads.filter(function(l){const t=l.tags||[];return t.indexOf("inbound-inquiry")>=0&&t.indexOf("inbound-callback")<0&&t.indexOf("inbound-statement")<0;}).length;
  if(inqCount>0)summaryBits.push(inqCount+" new web "+(inqCount>1?"inquiries":"inquiry"));
  if(optouts.length>0)summaryBits.push("🛑 "+optouts.length+" opt-out request"+(optouts.length>1?"s":"")+" — action required");

  const formatPref=function(tags){
    if(!tags)return "";
    if(tags.indexOf("pref-call")>=0)return "📞 Call";
    if(tags.indexOf("pref-email")>=0)return "✉️ Email";
    if(tags.indexOf("pref-either")>=0)return "Either";
    return "";
  };
  const formatLeadChips=function(tags){
    if(!tags)return "";
    const order=["time-morning","time-afternoon","time-evening","time-weekend","time-anytime"];
    return order.filter(function(t){return tags.indexOf(t)>=0;}).map(function(t){return t.slice(5);}).map(function(s){return s.charAt(0).toUpperCase()+s.slice(1);}).join(", ");
  };

  return(<div style={{position:"sticky",top:68,zIndex:85,padding:"0 24px",pointerEvents:"none"}}>
    <style>{`@keyframes urgentPulse{0%,100%{box-shadow:0 0 24px 0 rgba(239,68,68,0.55),inset 0 0 0 2px #ef4444}50%{box-shadow:0 0 38px 6px rgba(239,68,68,0.9),inset 0 0 0 2px #ef4444}}@keyframes urgentArrow{0%,100%{transform:translateY(0)}50%{transform:translateY(3px)}}`}</style>
    <div style={{margin:"10px auto 0",maxWidth:1400,pointerEvents:"auto",borderRadius:12,background:"linear-gradient(135deg,#7f1d1d,#991b1b)",color:"#fff",animation:"urgentPulse 1.6s ease-in-out infinite",overflow:"hidden",fontFamily:"'Mulish',system-ui,sans-serif"}}>
      <div style={{padding:"14px 20px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
        <div style={{fontSize:24,lineHeight:1}}>🚨</div>
        <div style={{flex:1,minWidth:200}}>
          <div style={{fontSize:13,fontWeight:800,letterSpacing:.4,textTransform:"uppercase",color:"#fff",opacity:.92}}>Urgent — needs attention</div>
          <div style={{fontSize:15,fontWeight:700,color:"#fff",marginTop:2}}>{summaryBits.join(" · ")}</div>
        </div>
        <button onClick={function(){sExpanded(function(p){return!p;});}} style={{padding:"8px 14px",borderRadius:8,border:"1px solid rgba(255,255,255,0.4)",background:"rgba(255,255,255,0.12)",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif"}}>{expanded?"Hide details":"Show details"}</button>
        <button onClick={markSeen} style={{padding:"8px 14px",borderRadius:8,border:"none",background:"#fff",color:"#991b1b",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif"}}>✓ Mark all seen</button>
      </div>
      {expanded&&<div style={{padding:"4px 20px 16px",borderTop:"1px solid rgba(255,255,255,0.2)"}}>
        {callbacks.map(function(x,i){
          const e=x.entry;const prefLabel=e.prefContact==="call"?"📞 Call":e.prefContact==="email"?"✉️ Email":e.prefContact==="either"?"Either":"";
          const chips=(e.bestTimeChips||[]).join(", ");
          return(<div key={"cb-"+i} onClick={function(){jump(x.loan);}} style={{padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.12)",cursor:"pointer",display:"flex",alignItems:"flex-start",gap:10}}>
            <span style={{fontSize:14}}>📞</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>{x.loan.b} <span style={{opacity:.7,fontWeight:500}}>requested a callback</span></div>
              <div style={{fontSize:11.5,color:"#fff",opacity:.88,marginTop:2}}>
                {prefLabel&&<span style={{marginRight:10}}>{prefLabel}</span>}
                {chips&&<span style={{marginRight:10}}>⏰ {chips}</span>}
                {e.bestTime&&<span style={{marginRight:10}}>"{e.bestTime}"</span>}
                {e.phone&&<span>📱 {e.phone}</span>}
              </div>
              {e.note&&<div style={{fontSize:11,color:"#fff",opacity:.75,marginTop:3,fontStyle:"italic"}}>"{e.note}"</div>}
              <div style={{fontSize:10,color:"#fff",opacity:.55,marginTop:3}}>{e.timestamp} · {x.loan.id}</div>
            </div>
          </div>);
        })}
        {uploads.map(function(x,i){
          return(<div key={"up-"+i} onClick={function(){jump(x.loan);}} style={{padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.12)",cursor:"pointer",display:"flex",alignItems:"flex-start",gap:10}}>
            <span style={{fontSize:14}}>📎</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>{x.loan.b} <span style={{opacity:.7,fontWeight:500}}>uploaded {x.entry.fileName||"a document"}</span></div>
              <div style={{fontSize:10,color:"#fff",opacity:.55,marginTop:3}}>{x.entry.timestamp} · {x.loan.id}</div>
            </div>
          </div>);
        })}
        {statements.map(function(x,i){
          return(<div key={"st-"+i} onClick={function(){jump(x.loan);}} style={{padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.12)",cursor:"pointer",display:"flex",alignItems:"flex-start",gap:10}}>
            <span style={{fontSize:14}}>📄</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>{x.loan.b} <span style={{opacity:.7,fontWeight:500}}>submitted statement info</span></div>
              <div style={{fontSize:11.5,color:"#fff",opacity:.85,marginTop:2}}>
                {x.entry.servicer&&<span style={{marginRight:10}}>Servicer: {x.entry.servicer}</span>}
                {x.entry.balance&&<span>Balance: {x.entry.balance}</span>}
              </div>
              <div style={{fontSize:10,color:"#fff",opacity:.55,marginTop:3}}>{x.entry.timestamp} · {x.loan.id}</div>
            </div>
          </div>);
        })}
        {portalOther.map(function(x,i){
          const cc=x.entry.kind==="contact-change";
          return(<div key={"po-"+i} onClick={function(){jump(x.loan);}} style={{padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.12)",cursor:"pointer",display:"flex",alignItems:"flex-start",gap:10}}>
            <span style={{fontSize:14}}>{cc?"✏️":"🎓"}</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>{x.loan.b} <span style={{opacity:.7,fontWeight:500}}>{cc?"changed contact info in the portal":"confirmed counseling"}</span></div>
              {x.entry.text&&<div style={{fontSize:11.5,color:"#fff",opacity:.85,marginTop:2}}>{x.entry.text}</div>}
              <div style={{fontSize:10,color:"#fff",opacity:.55,marginTop:3}}>{x.entry.timestamp} · {x.loan.id}</div>
            </div>
          </div>);
        })}
        {newLeads.map(function(l,i){
          const isCallback=(l.tags||[]).indexOf("inbound-callback")>=0;
          const isStatement=(l.tags||[]).indexOf("inbound-statement")>=0;
          const inq=!isCallback&&!isStatement?inquiryLabel(l.tags):null;
          const prefLabel=formatPref(l.tags);
          const chips=formatLeadChips(l.tags);
          return(<div key={"ml-"+i} onClick={function(){jumpLead(l);}} style={{padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.12)",cursor:"pointer",display:"flex",alignItems:"flex-start",gap:10}}>
            <span style={{fontSize:14}}>{isCallback?"📞":inq?"🌐":"📄"}</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>{l.name||"(no name)"} <span style={{opacity:.7,fontWeight:500}}>· {inq?inq+" (not yet a loan)":"portal "+(isCallback?"callback":"statement")+" (not yet a loan)"}</span></div>
              <div style={{fontSize:11.5,color:"#fff",opacity:.88,marginTop:2}}>
                {prefLabel&&<span style={{marginRight:10}}>{prefLabel}</span>}
                {chips&&<span style={{marginRight:10}}>⏰ {chips}</span>}
                {l.phone&&<span style={{marginRight:10}}>📱 {l.phone}</span>}
                {l.email&&<span>✉️ {l.email}</span>}
              </div>
              <div style={{fontSize:10,color:"#fff",opacity:.55,marginTop:3}}>{l.created_at?new Date(l.created_at).toLocaleString():""} · {l.id}</div>
            </div>
          </div>);
        })}
        {optouts.map(function(l,i){
          return(<div key={"oo-"+i} onClick={function(){jumpLead(l);}} style={{padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.12)",cursor:"pointer",display:"flex",alignItems:"flex-start",gap:10}}>
            <span style={{fontSize:14}}>🛑</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>{l.name||"(no name)"} <span style={{opacity:.7,fontWeight:500}}>· requested removal from mailings</span></div>
              <div style={{fontSize:11.5,color:"#fff",opacity:.88,marginTop:2}}>
                {l.address&&<span style={{marginRight:10}}>✉️ {l.address}</span>}
                {l.phone&&<span style={{marginRight:10}}>📱 {l.phone}</span>}
                {l.email&&<span>✉️ {l.email}</span>}
              </div>
              <div style={{fontSize:11,color:"#fff",opacity:.85,marginTop:3,fontWeight:600}}>Open → verify identity, then set matching record(s) to DNC (within 10 business days).</div>
              <div style={{fontSize:10,color:"#fff",opacity:.55,marginTop:3}}>{l.created_at?new Date(l.created_at).toLocaleString():""} · {l.id}</div>
            </div>
          </div>);
        })}
      </div>}
    </div>
  </div>);
}

// ═══════ WORKING LISTS (Call-ins to return · Numbers to run) ═══════
// Two funnel queues that sit above the pipeline: leads who reached out but
// haven't been spoken to yet, and leads spoken to whose numbers haven't been
// run. Deals enter automatically (new-lead intake, marketing promote) or via
// the Working List field in Loan Detail; every row offers the next concrete
// action plus "move it forward or archive it" controls.
// No quick "Log Call-In" form any more (Reece, 10/2: "now that we have a
// better and improved 'new call feature' we can get rid of the 'working lists
// clear' someone just called in item") — a caller is taken through New Call.
// Both lists empty → renders nothing at all.
// `loans` may be owner-filtered by the caller (Pipeline's Mine/Max/All); every
// write goes through setLoans on the full array, so filtering is display-only.
function WorkQueues({loans,setLoans,go,goCalc,onStartCall,staff,showOwner}){
  const me=staff||"reece";
  const callins=loans.filter(function(l){return !l.archived&&l.queue==="callin";});
  const numbers=loans.filter(function(l){return !l.archived&&l.queue==="numbers";});
  const stamp=function(){return new Date().toISOString();};
  const logTs=function(text){var ts=new Date();return{text:text,type:"system",timestamp:ts.toLocaleString(),date:ts.toLocaleDateString(),time:ts.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})};};
  // claim: "✓ Spoke" is a conversation — it claims an unowned file.
  const setQueue=function(loanId,q,logText,claim){
    setLoans(function(p){return p.map(function(l){if(l.id!==loanId)return l;var n=Object.assign({},l,{queue:q,queueAt:q?stamp():null,activityLog:(l.activityLog||[]).concat([logTs(logText)])});return claim?claimLoan(n,me):n;});});
  };
  const archive=function(l){
    if(!window.confirm("Move "+l.b+" ("+l.id+") off the active pipeline?\n\nThe file is archived (kept for reporting) — unarchive any time from Loan Detail."))return;
    setLoans(function(p){return p.map(function(x){return x.id===l.id?Object.assign({},x,{archived:true,queue:null,activityLog:(x.activityLog||[]).concat([logTs("Archived from working list")])}):x;});});
  };
  const ago=function(iso){if(!iso)return"";var d=Math.floor((Date.now()-new Date(iso).getTime())/86400000);return d<=0?"today":d===1?"1 day":d+" days";};
  // Interest level (src/interest.js): the name's colour + the booked
  // highlight, and the dot to set them — the same save as the board.
  const setInterest=function(id,patch){setLoans(function(p){return applyInterest(p,id,patch);});};
  const row=function(l,actions){const ist=interestStyle(l);return(<div key={l.id} data-queue-row={l.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:8,background:ist.booked?C.booked:C.surfaceAlt,border:"1px solid "+(ist.booked?C.bookedEdge:C.border),flexWrap:"wrap"}}>
    {showOwner!==false&&<OwnerChip owner={ownerOf(l)}/>}
    {setLoans&&<InterestMenu loan={l} onSet={function(patch){setInterest(l.id,patch);}}/>}
    <div onClick={function(){go&&go(l);}} style={{flex:1,minWidth:140,cursor:"pointer"}}>
      <div style={{fontSize:13,fontWeight:700,color:C.text}}><span data-interest-name={l.id} title={ist.label||undefined} style={interestNameStyle(l)}>{l.b}</span><span style={{fontSize:10,color:C.textDim,fontWeight:400,marginLeft:8}}>{l.id} · {l.src||"—"}{l.queueAt?" · waiting "+ago(l.queueAt):""}</span></div>
      <div style={{fontSize:11,color:C.textDim,marginTop:1}}>{l.ph||"no phone"}{l.prop?" · "+l.prop:""}</div>
    </div>
    {l.ph&&<CallBtn phone={l.ph} small/>}
    {/* Straight into the call console with this file loaded — find, price and
        disposition without leaving the queue. */}
    {onStartCall&&<Btn small primary onClick={function(){onStartCall({prospect:prospectFromLoan(l)});}}>{Ic.phone} Work it</Btn>}
    {actions}
  </div>);};
  // Nothing queued — nothing to show (no "lists clear" strip).
  if(callins.length===0&&numbers.length===0)return null;
  return(<div data-work-queues="1" style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(340px,1fr))",gap:16,marginBottom:24}}>
    {/* Call-ins to return */}
    <div style={{background:C.card,borderRadius:16,border:"1px solid "+C.cyan+"40",padding:"18px 20px"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
        <h3 style={{fontSize:14,fontWeight:800,color:C.cyan,margin:0}}>📞 Call-Ins — Not Yet Spoken To</h3>
        <span style={{fontSize:11,fontWeight:700,color:C.cyan,background:C.cyan+"18",padding:"1px 8px",borderRadius:10}}>{callins.length}</span>
      </div>
      <div style={{fontSize:10.5,color:C.textDim,marginBottom:10}}>Everyone who has reached out that you still need to talk to. After the conversation, funnel the deal forward — or archive it off the active sheet.</div>
      <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:280,overflowY:"auto"}}>
        {callins.length===0&&<div style={{padding:14,textAlign:"center",fontSize:12,color:C.textDim,border:"1px dashed "+C.border,borderRadius:8}}>Nobody waiting on a call back</div>}
        {callins.map(function(l){return row(l,<>
          <Btn small primary onClick={function(){setQueue(l.id,"numbers","Spoke with borrower — moved to Run-the-Numbers list",true);}}>✓ Spoke</Btn>
          <Btn small onClick={function(){setQueue(l.id,null,"Removed from call-in list");}} style={{color:C.textDim,fontSize:10}}>Remove</Btn>
          <Btn small onClick={function(){archive(l);}} style={{color:C.red,fontSize:10}}>Archive</Btn>
        </>);})}
      </div>
    </div>
    {/* Spoken to — numbers not run */}
    <div style={{background:C.card,borderRadius:16,border:"1px solid "+C.orange+"40",padding:"18px 20px"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
        <h3 style={{fontSize:14,fontWeight:800,color:C.orange,margin:0}}>🧮 Spoken To — Numbers Not Run</h3>
        <span style={{fontSize:11,fontWeight:700,color:C.orange,background:C.orange+"18",padding:"1px 8px",borderRadius:10}}>{numbers.length}</span>
      </div>
      <div style={{fontSize:10.5,color:C.textDim,marginBottom:10}}>You've had the conversation — now price it. Run the calculator and pitch a scenario to keep the deal funneling down.</div>
      <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:280,overflowY:"auto"}}>
        {numbers.length===0&&<div style={{padding:14,textAlign:"center",fontSize:12,color:C.textDim,border:"1px dashed "+C.border,borderRadius:8}}>No priced-out conversations pending</div>}
        {numbers.map(function(l){return row(l,<>
          {goCalc&&<Btn small primary onClick={function(){goCalc(l);}}>{Ic.calc} Run Numbers</Btn>}
          <Btn small onClick={function(){setQueue(l.id,null,"Removed from run-the-numbers list");}} style={{color:C.textDim,fontSize:10}}>Remove</Btn>
          <Btn small onClick={function(){archive(l);}} style={{color:C.red,fontSize:10}}>Archive</Btn>
        </>);})}
      </div>
    </div>
  </div>);
}

// ═══════ FOLLOW-UPS ═══════
// Reece, 10/2: "get rid of the 'upcoming' screen on the command center. I'll
// never ever need that. I do want some sort of follow up or notification
// screen for notes and appointments to follow up on.. But i don't need the
// to do list like that." So the Today / Upcoming task lists are gone and this
// is the one list (it also replaces the old Call Backs Due card, which was
// the same follow-up dates): every follow-up date he set, with its note, on
// files, fallout files and mail-list leads — plus the appointments on live
// files: counseling, the appraisal visit and the closing (lib.js
// collectFollowUpItems has the rules). Grouped Overdue · Today · Next 7 days.
// A call back (a follow-up date) opens New Call on that person — the file
// card for a file, the lead card for a lead — where it can be worked,
// rescheduled or cleared (as the old Call Backs Due card did; Loan Detail
// only shows the date). A counseling / appraisal / closing row opens the file
// in Loan Detail. The top-bar bell counts Overdue + Today and lands here
// (focusKey).
const FU_GROUPS=[
  {k:"overdue",l:"Overdue",color:C.red},
  {k:"today",l:"Today",color:C.orange},
  {k:"week",l:"Next "+FOLLOWUP_HORIZON_DAYS+" days",color:C.accent},
];
const FU_ICONS={followup:"📞",counseling:"🎓",appraisal:"🏠",closing:"🔑"};
const FU_WEEKDAYS=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const FU_ROWS=8;
// "Today · 10:00 AM" / "Tomorrow" / "Thu 10/8" / "9/28 · 4 days overdue"
function fuWhen(it,today){
  const d=parseDateLocal(it.date);const t=parseDateLocal(today);
  if(!d||!t)return it.date;
  const n=Math.round((d.getTime()-t.getTime())/86400000);
  const md=(d.getMonth()+1)+"/"+d.getDate();
  const tm=it.time?" · "+it.time:"";
  if(n===0)return"Today"+tm;
  if(n<0)return md+" · "+(-n)+" day"+(n===-1?"":"s")+" overdue";
  return(n===1?"Tomorrow":FU_WEEKDAYS[d.getDay()]+" "+md)+tm;
}
// What it is: the follow-up's note (or "Follow up"), else the appointment.
function fuWhat(it){
  if(it.what==="followup")return it.note||"Follow up";
  return it.note+(it.time?" at "+it.time:"");
}
function FollowUpsPanel({items,today,loans,setNav,setSel,onStartCall,setMktgLeadFocus,focusKey}){
  const [showAll,setShowAll]=useState({});
  const ref=useRef(null);
  const loanById=useMemo(function(){return new Map((loans||[]).map(function(l){return [l.id,l];}));},[loans]);
  // The bell sent him here — bring the panel into view.
  useEffect(function(){
    if(!focusKey)return;
    const el=ref.current;
    if(el&&typeof el.scrollIntoView==="function"){try{el.scrollIntoView({behavior:"smooth",block:"start"});}catch(e){/* older browsers */}}
  },[focusKey]);
  const open=function(it){
    // A file's call back → New Call on the file (Loan Detail can't move it).
    if(it.kind==="loan"&&it.what==="followup"&&onStartCall){onStartCall({prospect:prospectFromLoan(it.raw)});return;}
    if(it.kind==="loan"){if(setSel)setSel(it.raw);if(setNav)setNav("loanDetail");return;}
    if(onStartCall){onStartCall({prospect:prospectFromLead(it.raw)});return;}
    if(setMktgLeadFocus&&setNav){setMktgLeadFocus(it.raw);setNav("marketing");}
  };
  const counts={overdue:0,today:0,week:0};
  items.forEach(function(it){counts[it.group]++;});
  const dueNow=counts.overdue+counts.today;
  return(<div id="cc-followups" ref={ref} data-testid="followups-panel" style={{background:C.card,borderRadius:16,border:"1px solid "+(counts.overdue>0?C.red+"45":dueNow>0?C.orange+"50":C.border),padding:"18px 22px",marginBottom:24,scrollMarginTop:90}}>
    <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:4,flexWrap:"wrap"}}>
      <h3 style={{margin:0,fontSize:15,fontWeight:800,color:C.text,letterSpacing:.2}}>📅 Follow-ups</h3>
      {FU_GROUPS.map(function(g){return counts[g.k]>0?<span key={g.k} style={{fontSize:11,fontWeight:800,color:g.color,background:g.color+"18",padding:"1px 9px",borderRadius:10}}>{g.l} {counts[g.k]}</span>:null;})}
    </div>
    <div style={{fontSize:10.5,color:C.textDim,marginBottom:12}}>Follow-up dates and notes you set on files, fallout files and leads, plus counseling, appraisal and closing dates on live files. Click one to open it — a call back opens New Call on that person (to work, reschedule or clear it); an appointment opens the file in Loan Detail.</div>
    {items.length===0?
      <div style={{padding:"12px 14px",fontSize:12,color:C.textDim,border:"1px dashed "+C.border,borderRadius:8}}>Nothing to follow up on — no follow-ups or appointments in the next {FOLLOWUP_HORIZON_DAYS} days.</div>:
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        {FU_GROUPS.map(function(g){
          const list=items.filter(function(it){return it.group===g.k;});
          if(!list.length)return null;
          const rows=showAll[g.k]?list:list.slice(0,FU_ROWS);
          return(<div key={g.k} data-followup-group={g.k}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <span style={{width:8,height:8,borderRadius:"50%",background:g.color,flexShrink:0}}/>
              <span style={{fontSize:11,fontWeight:800,color:g.color,textTransform:"uppercase",letterSpacing:.6}}>{g.l}</span>
              <span style={{fontSize:11,color:C.textDim}}>{list.length}</span>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {rows.map(function(it){
                const owner=it.kind==="loan"?ownerOf(it.raw):leadOwnerShown(it.raw,loanById);
                // A file's row carries its interest level (a lead has none).
                const ist=interestStyle(it.kind==="loan"?it.raw:null);
                return(<div key={it.key} data-followup-item={it.key} data-followup-what={it.what} role="button" tabIndex={0}
                  onClick={function(){open(it);}} onKeyDown={function(e){
                    // Only the row itself: Enter on the call pill inside it dials.
                    if(e.target!==e.currentTarget)return;
                    if(e.key==="Enter"||e.key===" "){e.preventDefault();open(it);}}}
                  title={it.kind==="loan"&&it.what!=="followup"?"Open "+it.id+" in Loan Detail":"Open "+(it.name||(it.kind==="loan"?it.id:"this lead"))+" in New Call"}
                  style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:8,background:ist.booked?C.booked:C.surfaceAlt,border:"1px solid "+(g.k==="overdue"?C.red+"35":ist.booked?C.bookedEdge:C.border),cursor:"pointer",flexWrap:"wrap"}}>
                  {owner&&<OwnerChip owner={owner}/>}
                  <span aria-hidden="true" style={{fontSize:14,flexShrink:0}}>{FU_ICONS[it.what]}</span>
                  <div style={{flex:"1 1 200px",minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}><span data-interest-name={it.kind==="loan"?it.id:undefined} title={ist.label||undefined} style={it.kind==="loan"?interestNameStyle(it.raw):undefined}>{it.name}</span>
                      <span style={{fontSize:10,color:C.textDim,fontWeight:400,marginLeft:8}}>{it.kind==="loan"?"file "+it.id:"lead"}{it.label?" · "+it.label:""}</span>
                    </div>
                    <div style={{fontSize:11.5,color:it.what==="followup"?C.textMuted:C.accentLight,fontWeight:it.what==="followup"?500:700,marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{fuWhat(it)}</div>
                  </div>
                  {it.optedOut&&<span title="Off mailings and the Outbound dialer — calling them back is fine" style={{fontSize:10.5,color:C.textDim}}>Opted out · off mailings</span>}
                  {it.phone&&<span onClick={function(e){e.stopPropagation();}} style={{display:"inline-flex"}}><CallBtn phone={it.phone} small/></span>}
                  <Badge label={fuWhen(it,today)} color={g.color} bg={g.color+"18"}/>
                </div>);
              })}
              {list.length>FU_ROWS&&<Btn small onClick={function(){setShowAll(function(p){return Object.assign({},p,{[g.k]:!p[g.k]});});}} style={{color:C.accentLight,alignSelf:"flex-start"}}>
                {showAll[g.k]?"Show fewer":"Show all "+list.length}
              </Btn>}
            </div>
          </div>);
        })}
      </div>}
  </div>);
}

// ═══════ REIMBURSEMENTS OWED (10/10/26) ═══════
// Money Reece paid on a file (credit report, appraisal…) and is owed back —
// set in Loan Detail's "Reimbursements owed to you" (src/reimbursements.js).
// Every file with something owed, any stage (a fallout or archived file
// included — the money is still owed), oldest Date paid first; per-file total
// and the grand total. A row opens the file. Nothing owed → not rendered.
function ReimbursementsOwedPanel({loans,open}){
  const owed=useMemo(function(){return owedByFile(loans);},[loans]);
  if(!owed.files.length)return null;
  return(<div id="cc-reimbursements" data-testid="reimbursements-panel" style={{background:C.card,borderRadius:16,border:"1px solid "+C.clay+"55",padding:"18px 22px",marginBottom:24,scrollMarginTop:90}}>
    <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:4,flexWrap:"wrap"}}>
      <h3 style={{margin:0,fontSize:15,fontWeight:800,color:C.text,letterSpacing:.2}}>Reimbursements owed</h3>
      <span data-reimb-grand-total="1" style={{fontSize:12,fontWeight:800,color:C.clayDeep,background:C.white,border:"1px solid "+C.clay+"99",padding:"1px 10px",borderRadius:10}}>{fmtMoney(owed.total)}</span>
      <span style={{fontSize:11,color:C.textDim}}>{owed.files.length} file{owed.files.length===1?"":"s"}</span>
    </div>
    <div style={{fontSize:10.5,color:C.textDim,marginBottom:12}}>What you paid on a file and haven't been paid back for — oldest first. Click one to open the file and mark it reimbursed.</div>
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      {owed.files.map(function(f){
        const l=f.loan;const ist=interestStyle(l);
        return(<div key={l.id} data-reimb-file={l.id} role="button" tabIndex={0}
          onClick={function(){open(l);}} onKeyDown={function(e){if(e.target!==e.currentTarget)return;if(e.key==="Enter"||e.key===" "){e.preventDefault();open(l);}}}
          title={"Open "+l.id+" in Loan Detail"}
          style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:8,background:C.white,border:"1px solid "+C.clay+"40",cursor:"pointer",flexWrap:"wrap"}}>
          <OwnerChip owner={ownerOf(l)}/>
          <div style={{flex:"1 1 220px",minWidth:0}}>
            <div style={{fontSize:13,fontWeight:700,color:C.text}}><span data-interest-name={l.id} title={ist.label||undefined} style={interestNameStyle(l)}>{l.b||"(unnamed)"}</span>
              <span style={{fontSize:10,color:C.textDim,fontWeight:400,marginLeft:8}}>file {l.id} · {l.stage}</span>
            </div>
            <div style={{fontSize:11.5,color:C.textMuted,marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
              {f.items.map(function(x){return x.kind+" "+fmtMoney(x.amount);}).join(" · ")}{f.oldest?" — oldest paid "+fmtDay(f.oldest):""}
            </div>
          </div>
          <span data-reimb-file-total={l.id} style={{fontSize:14,fontWeight:800,color:C.clayDeep}}>{fmtMoney(f.total)}</span>
        </div>);
      })}
    </div>
  </div>);
}

// ═══════ DASHBOARD (Command Center) ═══════
// Numbers stay COMBINED for both staff (one shared pipeline); every listed
// file carries its owner chip so it's clear whose it is at a glance — only
// while "Lead assignment & Max on files" is on (src/features.js; off by
// default: no chips, no Owner column, every file listed as always).
// focusFollowUps: bumped by the top-bar bell — the Follow-ups panel scrolls
// itself into view.
function Dashboard({loans,setLoans,expenses,campaigns,mktgLeads,setMktgLeads,leadCounts,leadsReady,setNav,setSel,setMktgLeadFocus,onStartCall,staff,registerFlush,focusFollowUps,settings,rcAuthed,adoptLeads,allLeadsLoaded,practice}){
  const ownershipUi=useOwnershipUi();
  // Follow-ups + appointments (overdue · today · next 7 days) — the panel and
  // the stat tile read the same list.
  const today=isoDatePlus(0);
  const fuItems=useMemo(function(){return collectFollowUpItems(loans,mktgLeads,today,FOLLOWUP_HORIZON_DAYS);},[loans,mktgLeads,today]);
  const fuOverdue=fuItems.filter(function(it){return it.group==="overdue";}).length;
  const fuToday=fuItems.filter(function(it){return it.group==="today";}).length;
  const fuWeek=fuItems.length-fuOverdue-fuToday;
  // The Whiteboard panel: the Command Center's own login (Reece) only.
  const acc=staffAccess(staff);
  const wbOn=!!acc.dashboard&&!!acc.whiteboard;
  // Exclude archived files from all dashboard counts — they live in the archive, not the active pipeline
  const activeLoans=loans.filter(l=>!l.archived).sort((a,b)=>STAGES.indexOf(a.stage)-STAGES.indexOf(b.stage));
  const totalComm=activeLoans.reduce((a,l)=>a+l.comm,0);
  const urgentTasks=activeLoans.reduce((a,l)=>a+l.tasks.filter(t=>t.startsWith("URGENT")||t.startsWith("CALLBACK")).length,0);
  const overdueFU=activeLoans.reduce((a,l)=>{Object.values(l.followUps).forEach(d=>{if(d>=3)a++;});return a;},0);
  const $=n=>"$"+Math.round(n).toLocaleString();

  // ── Analytics ──
  const closed=activeLoans.filter(l=>l.stage==="Funded"||l.stage==="Closed");
  const closedComm=closed.reduce((a,l)=>a+l.comm,0);
  // The ten stages in three buckets (Prospect, Closed and Funded sit outside
  // them): sales → processing → near closing.
  const nearClose=activeLoans.filter(l=>l.stage==="Approved"||l.stage==="Clear to Close");
  const nearCloseComm=nearClose.reduce((a,l)=>a+l.comm,0);
  const inSales=activeLoans.filter(l=>l.stage==="Pitched"||l.stage==="Scheduled Counseling"||l.stage==="Docs Out");
  const inProc=activeLoans.filter(l=>l.stage==="Appraisal Ordered"||l.stage==="Underwriting");
  const totalExpenses=(expenses||[]).reduce((a,e)=>a+e.amount,0);
  const netRevenue=closedComm-totalExpenses;
  const avgComp=activeLoans.length>0?Math.round(totalComm/activeLoans.length):0;
  const closeRate=activeLoans.length>0?Math.round((closed.length/activeLoans.length)*100):0;
  const mktgExpenses=(expenses||[]).filter(e=>e.cat==="Marketing").reduce((a,e)=>a+e.amount,0);
  const totalCampaigns=(campaigns||[]).length;
  // Before the lazy full load, mktgLeads holds only alert rows — use the
  // head-count numbers fetched at login instead.
  // Only the working set (mailed/worked leads) is in memory, so totals come
  // from the server head-count; the in-memory list is a fallback for local dev.
  const totalMktgLeads=leadCounts&&leadCounts.total!=null?leadCounts.total:(mktgLeads||[]).length;
  const contactable=leadCounts&&leadCounts.contactable!=null?leadCounts.contactable:(mktgLeads||[]).filter(l=>!l.doNotContact).length;

  // Pipeline stage counts for mini chart — active loans only
  const stageCounts=STAGES.map(s=>({stage:s,count:activeLoans.filter(l=>l.stage===s).length,color:STC[s]}));
  const maxCount=Math.max(...stageCounts.map(s=>s.count),1);

  return(<div>
    <Hdr t="Command Center" s="Follow-ups, pipeline health, and operations at a glance"/>

    {/* ── CALLS TO RETURN (10/10) — missed calls / voicemails not returned yet
        and how long each has waited, from RingCentral's call log. Reece only;
        a list, never a bell, badge or reminder (src/views/Callbacks.jsx). ── */}
    <CallbacksPanel staff={staff} settings={settings} rcAuthed={rcAuthed} loans={loans} mktgLeads={mktgLeads} adoptLeads={adoptLeads} allLeadsLoaded={allLeadsLoaded} onStartCall={onStartCall} practice={practice}/>

    {/* ── STATEMENT SUBMISSIONS — permanent home, first thing on the page ── */}
    <WebLeads mktgLeads={mktgLeads} setMktgLeads={setMktgLeads} loans={loans} setLoans={setLoans} setNav={setNav} setSel={setSel} onStartCall={onStartCall} staff={staff} registerFlush={registerFlush}/>

    {/* ── TOP STATS ── */}
    <div style={{display:"flex",gap:14,marginBottom:24,flexWrap:"wrap"}}>
      <Stat label="Pipeline Value" value={$(totalComm)} sub={activeLoans.length+" active files"} color={C.gold} icon={Ic.dollar}/>
      <Stat label="Near Closing" value={$(nearCloseComm)} sub={nearClose.length+" files"} color={C.green} icon={Ic.chk}/>
      <Stat label="Follow-ups Due" value={fuOverdue+fuToday} sub={fuOverdue>0?fuOverdue+" overdue"+(fuToday>0?" · "+fuToday+" today":""):fuToday>0?fuToday+" due today":fuWeek>0?fuWeek+" in the next "+FOLLOWUP_HORIZON_DAYS+" days":"All clear"} color={fuOverdue>0?C.red:C.orange} icon={Ic.clock}/>
      <Stat label="Avg Comp" value={$(avgComp)} sub="Per file" color={C.accent} icon={Ic.calc}/>
    </div>

    {/* ── FOLLOW-UPS — follow-up dates + notes, and appointments (the top-bar
        bell lands here). Replaces the old Today / Upcoming to-do lists. ── */}
    <FollowUpsPanel items={fuItems} today={today} loans={loans} setNav={setNav} setSel={setSel} onStartCall={onStartCall} setMktgLeadFocus={setMktgLeadFocus} focusKey={focusFollowUps}/>

    {/* ── WHITEBOARD (10/2) — Reece's misc notes, right under Follow-ups: the
        same board as the bottom-left bubble (src/whiteboard.js). Free text,
        not a to-do list — nothing here feeds the bell or the tile above. ── */}
    {wbOn&&<WhiteboardPanel staff={staff}/>}

    {/* ── WORKING LISTS — call-ins to return + numbers to run (nothing when both are empty) ── */}
    <WorkQueues loans={loans} setLoans={setLoans} go={function(l){setSel(l);setNav("loanDetail");}} goCalc={function(l){setSel(l);setNav("inquiry");}} onStartCall={onStartCall} staff={staff}/>

    {/* ── REIMBURSEMENTS OWED (10/10) — credit reports, appraisals… paid on a
        file and not paid back yet (hidden when nothing is owed). ── */}
    <ReimbursementsOwedPanel loans={loans} open={function(l){setSel(l);setNav("loanDetail");}}/>

    {/* ── PIPELINE FUNNEL ── */}
    <div style={{background:C.card,borderRadius:16,border:"1px solid "+C.border,padding:"22px 24px",marginBottom:24}}>
      <h3 style={{fontSize:14,fontWeight:700,color:C.text,margin:"0 0 14px",letterSpacing:.2}}>Pipeline Funnel</h3>
      {stageCounts.map(s=><div key={s.stage} style={{display:"flex",alignItems:"center",gap:12,marginBottom:7}}>
        <span style={{fontSize:10.5,color:C.textDim,minWidth:100,textAlign:"right"}}>{s.stage}</span>
        <div style={{flex:1,height:16,background:C.surfaceAlt,borderRadius:6,overflow:"hidden"}}>
          <div style={{width:Math.max(s.count/maxCount*100,s.count>0?8:0)+"%",height:"100%",background:"linear-gradient(90deg,"+s.color+"CC,"+s.color+")",borderRadius:6,transition:"width 0.3s"}}/>
        </div>
        <span style={{fontSize:12,fontWeight:700,color:s.color,minWidth:22,fontFamily:"'Mulish',system-ui,sans-serif"}}>{s.count}</span>
      </div>)}
    </div>

    {/* ── ANALYTICS ROW ── */}
    <div style={{display:"flex",gap:14,marginBottom:20,flexWrap:"wrap"}}>
      <div style={{flex:"1 1 200px",background:C.card,borderRadius:12,border:"1px solid "+C.border,padding:"16px 20px"}}>
        <div style={{fontSize:10,fontWeight:700,color:C.textDim,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Revenue vs Expenses</div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
          <div><div style={{fontSize:11,color:C.textDim}}>Closed Comp</div><div style={{fontSize:20,fontWeight:700,color:C.green,fontFamily:"'Mulish',system-ui,sans-serif"}}>{$(closedComm)}</div></div>
          <div style={{textAlign:"center"}}><div style={{fontSize:11,color:C.textDim}}>Expenses</div><div style={{fontSize:20,fontWeight:700,color:C.red,fontFamily:"'Mulish',system-ui,sans-serif"}}>{$(totalExpenses)}</div></div>
          <div style={{textAlign:"right"}}><div style={{fontSize:11,color:C.textDim}}>Net</div><div style={{fontSize:20,fontWeight:700,color:netRevenue>=0?C.green:C.red,fontFamily:"'Mulish',system-ui,sans-serif"}}>{$(netRevenue)}</div></div>
        </div>
      </div>
      <div style={{flex:"1 1 200px",background:C.card,borderRadius:12,border:"1px solid "+C.border,padding:"16px 20px"}}>
        <div style={{fontSize:10,fontWeight:700,color:C.textDim,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Pipeline Velocity</div>
        <div style={{display:"flex",justifyContent:"space-between"}}>
          <div><div style={{fontSize:11,color:C.textDim}}>In Sales</div><div style={{fontSize:20,fontWeight:700,color:C.orange,fontFamily:"'Mulish',system-ui,sans-serif"}}>{inSales.length}</div></div>
          <div style={{textAlign:"center"}}><div style={{fontSize:11,color:C.textDim}}>In Processing</div><div style={{fontSize:20,fontWeight:700,color:C.accent,fontFamily:"'Mulish',system-ui,sans-serif"}}>{inProc.length}</div></div>
          <div style={{textAlign:"right"}}><div style={{fontSize:11,color:C.textDim}}>Close Rate</div><div style={{fontSize:20,fontWeight:700,color:C.purple,fontFamily:"'Mulish',system-ui,sans-serif"}}>{closeRate}%</div></div>
        </div>
      </div>
      <div style={{flex:"1 1 200px",background:C.card,borderRadius:12,border:"1px solid "+C.border,padding:"16px 20px"}}>
        <div style={{fontSize:10,fontWeight:700,color:C.textDim,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Marketing</div>
        <div style={{display:"flex",justifyContent:"space-between"}}>
          <div><div style={{fontSize:11,color:C.textDim}}>DB Leads</div><div style={{fontSize:20,fontWeight:700,color:C.accent,fontFamily:"'Mulish',system-ui,sans-serif"}}>{totalMktgLeads}</div></div>
          <div style={{textAlign:"center"}}><div style={{fontSize:11,color:C.textDim}}>Campaigns</div><div style={{fontSize:20,fontWeight:700,color:C.cyan,fontFamily:"'Mulish',system-ui,sans-serif"}}>{totalCampaigns}</div></div>
          <div style={{textAlign:"right"}}><div style={{fontSize:11,color:C.textDim}}>Mktg Spend</div><div style={{fontSize:20,fontWeight:700,color:C.orange,fontFamily:"'Mulish',system-ui,sans-serif"}}>{$(mktgExpenses)}</div></div>
        </div>
      </div>
    </div>

    {/* Urgent Items */}
    {(urgentTasks>0||overdueFU>0)&&<div style={{background:C.redBg,border:`1px solid ${C.red}30`,borderRadius:12,padding:"16px 20px",marginBottom:20}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}><span style={{color:C.red}}>{Ic.warn}</span><span style={{fontSize:14,fontWeight:700,color:C.red}}>Needs Attention</span></div>
      {/* Same set the gate above counted — active files only. Listing every
          loan dragged archived files (the old seed demos) in here. */}
      {activeLoans.filter(l=>(l.tasks||[]).some(t=>t.startsWith("URGENT")||t.startsWith("CALLBACK"))||Object.values(l.followUps||{}).some(d=>d>=3)).map(l=><div key={l.id} onClick={()=>{setSel(l);setNav("loanDetail");}} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.red}15`,cursor:"pointer"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}><OwnerChip owner={ownerOf(l)}/><span style={{fontSize:12,fontFamily:"'Mulish',system-ui,sans-serif",color:C.accentLight}}>{l.id}</span><span data-interest-name={l.id} title={interestStyle(l).label||undefined} style={Object.assign({fontSize:13,color:C.text,marginLeft:2,fontWeight:interestStyle(l).level?800:500},interestNameStyle(l),interestStyle(l).booked?{background:C.booked,padding:"1px 6px",borderRadius:5}:null)}>{l.b}</span></div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{(l.tasks||[]).filter(t=>t.startsWith("URGENT")||t.startsWith("CALLBACK")).map((t,i)=><Badge key={i} label={t.replace(/^(URGENT|CALLBACK):\s*/,"")} color={t.startsWith("URGENT")?C.red:C.orange} bg={t.startsWith("URGENT")?`${C.red}25`:C.orangeBg}/>)}{Object.entries(l.followUps||{}).filter(([,d])=>d>=3).map(([k,d])=><Badge key={k} label={`${k.toUpperCase()} ${d}d overdue`} color={C.orange} bg={C.orangeBg}/>)}</div>
      </div>)}
    </div>}

    {/* EPO Alerts */}
    {(function(){
      var today=new Date();
      var parseMDY=function(s){if(!s)return null;var p=s.split("/");if(p.length===3){var m=parseInt(p[0]),d=parseInt(p[1]),y=parseInt(p[2]);if(!isNaN(m)&&!isNaN(d)&&!isNaN(y))return new Date(y,m-1,d);}var dt=new Date(s);return isNaN(dt.getTime())?null:dt;};
      var epoActive=loans.filter(function(l){
        if(l.epo_status==="triggered")return true;
        if((l.stage==="Funded"||l.stage==="Closed")&&l.fundedDate&&l.epo_status!=="clear"&&l.epo_status!=="resolved"){
          var fd=parseMDY(l.fundedDate);
          if(fd){var days=Math.floor((today-fd)/(24*60*60*1000));return days>=0&&days<183;}
        }
        return false;
      });
      if(epoActive.length===0)return null;
      return(<div style={{background:C.orangeBg,border:"1px solid "+C.orange+"40",borderRadius:12,padding:"16px 20px",marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <span style={{fontSize:16}}>⚠️</span>
          <span style={{fontSize:14,fontWeight:700,color:C.orange}}>EPO Watch — {epoActive.length} file{epoActive.length>1?"s":""} in chargeback window</span>
        </div>
        {epoActive.map(function(l){
          var fd=parseMDY(l.fundedDate);
          var daysLeft=fd?Math.max(0,183-Math.floor((today-fd)/(24*60*60*1000))):null;
          return(<div key={l.id} onClick={function(){setSel(l);setNav("loanDetail");}} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 10px",borderRadius:8,background:C.orange+"0A",border:"1px solid "+C.orange+"25",marginBottom:4,cursor:"pointer"}} onMouseEnter={function(e){e.currentTarget.style.background=C.orange+"15";}} onMouseLeave={function(e){e.currentTarget.style.background=C.orange+"0A";}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <OwnerChip owner={ownerOf(l)}/>
              <span style={{fontSize:12,fontFamily:"'Mulish',system-ui,sans-serif",color:C.accentLight}}>{l.id}</span>
              <span data-interest-name={l.id} title={interestStyle(l).label||undefined} style={Object.assign({fontSize:13,color:C.text,fontWeight:interestStyle(l).level?800:500},interestNameStyle(l),interestStyle(l).booked?{background:C.booked,padding:"1px 6px",borderRadius:5}:null)}>{l.b}</span>
              {l.epo_status==="triggered"&&<Badge label="EPO Triggered" color={C.red} bg={C.redBg}/>}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              {daysLeft!==null&&l.epo_status!=="triggered"&&<span style={{fontSize:12,fontWeight:700,color:daysLeft<30?C.red:C.orange,fontFamily:"'Mulish',system-ui,sans-serif"}}>{daysLeft}d remaining</span>}
              {l.epo_chargebackAmt&&l.epo_status==="triggered"&&<span style={{fontSize:12,fontWeight:700,color:C.red,fontFamily:"'Mulish',system-ui,sans-serif"}}>${parseInt(l.epo_chargebackAmt).toLocaleString()} chargeback</span>}
              <span style={{fontSize:11,color:C.textDim}}>→</span>
            </div>
          </div>);
        })}
      </div>);
    })()}

    {/* Quick Actions */}
    <div style={{display:"flex",gap:10,marginBottom:24,flexWrap:"wrap"}}>
      {[{l:"Pipeline",n:"pipeline",i:Ic.pipe,c:C.accent},{l:"Inquiry",n:"inquiry",i:Ic.intake,c:C.orange},{l:"Marketing",n:"marketing",i:Ic.mktg,c:C.cyan},{l:"Expenses",n:"expenses",i:Ic.receipt,c:C.purple},{l:"Settings",n:"settings",i:Ic.lock,c:C.gold}].map(x=><button key={x.n} onClick={()=>setNav(x.n)} style={{padding:"12px 16px",borderRadius:10,border:"1px solid "+C.border,background:C.card,color:C.text,fontSize:12.5,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontFamily:"'Mulish',system-ui,sans-serif",transition:"background .15s, border-color .15s"}} onMouseEnter={e=>{e.currentTarget.style.background=C.cardHover;e.currentTarget.style.borderColor=x.c+"40";}} onMouseLeave={e=>{e.currentTarget.style.background=C.card;e.currentTarget.style.borderColor=C.border;}}><span style={{color:x.c}}>{x.i}</span>{x.l}</button>)}
    </div>

    {/* File Overview */}
    <div style={{background:C.card,borderRadius:14,border:`1px solid ${C.border}`,overflow:"hidden"}}>
      <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}><span style={{fontWeight:700,fontSize:14}}>All Active Files</span><div style={{display:"flex",alignItems:"center",gap:10}}><span style={{fontSize:12,color:C.textDim}}>{activeLoans.length} active {loans.filter(l=>l.archived).length>0?"("+loans.filter(l=>l.archived).length+" archived)":""} • {$(totalComm)} pipeline</span><Btn small onClick={function(){
        // The Owner column only while the owner UI is on (src/features.js).
        var rows=activeLoans.map(function(l){return[l.id,STAFF[ownerOf(l)]?STAFF[ownerOf(l)].name:ownerOf(l),l.b,l.stage,l.type,l.comm,l.estClose,l.src,l.ref||"",l.prop,l.ph,l.em];});
        var head=["Loan ID","Owner","Borrower","Stage","Type","Commission","Est Close","Source","Referral","Property","Phone","Email"];
        if(!ownershipUi){head.splice(1,1);rows=rows.map(function(r){return r.filter(function(_,i){return i!==1;});});}
        exportCSV("pipeline_export.csv",head,rows);
      }} style={{fontSize:10,color:C.accent}}>Export CSV</Btn></div></div>
      <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",minWidth:750}}>
        <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>{(ownershipUi?["Owner"]:[]).concat(["Loan","Borrower","Stage","Type","Est. Close","Commission","Tasks"]).map(h=><th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:10,fontWeight:700,color:C.textDim,textTransform:"uppercase",letterSpacing:.5}}>{h}</th>)}</tr></thead>
        <tbody>{activeLoans.map(l=>{const ist=interestStyle(l);const rowBg=ist.booked?C.booked:"transparent";return <tr key={l.id} data-file-row={l.id} onClick={()=>{setSel(l);setNav("loanDetail");}} style={{borderBottom:`1px solid ${C.border}`,cursor:"pointer",background:rowBg}} onMouseEnter={e=>e.currentTarget.style.background=ist.booked?C.bookedHover:C.cardHover} onMouseLeave={e=>e.currentTarget.style.background=rowBg}>
          {ownershipUi&&<td data-owner-cell={l.id} style={{padding:"10px 14px"}}><OwnerChip owner={ownerOf(l)}/></td>}
          <td style={{padding:"10px 14px",fontSize:12,fontFamily:"'Mulish',system-ui,sans-serif",color:C.accentLight}}>{l.id}</td>
          <td data-interest-name={l.id} title={ist.label||undefined} style={Object.assign({padding:"10px 14px",fontSize:13,fontWeight:ist.level?800:500},interestNameStyle(l))}>{l.b}</td>
          <td style={{padding:"10px 14px"}}><Badge label={l.stage} color={STC[l.stage]} bg={`${STC[l.stage]}18`}/></td>
          <td style={{padding:"10px 14px"}}><Badge label={l.type} color={C.accent} bg={C.accentGlow}/></td>
          <td style={{padding:"10px 14px",fontSize:12,color:C.textMuted}}>{l.estClose}</td>
          <td style={{padding:"10px 14px",fontSize:13,fontWeight:600,color:C.gold,fontFamily:"'Mulish',system-ui,sans-serif"}}>${l.comm.toLocaleString()}{l.deal&&l.deal.manual&&<span title="Figures typed from the lender's paperwork, not priced by the calculator" style={{marginLeft:8,fontSize:10,fontWeight:700,color:C.purple}}>{MANUAL_DEAL_LABEL}</span>}</td>
          <td style={{padding:"10px 14px"}}>{l.tasks.length>0?<Badge label={`${l.tasks.length} ${l.tasks.some(t=>t.startsWith("URGENT"))?"!":""}`} color={l.tasks.some(t=>t.startsWith("URGENT"))?C.red:C.orange} bg={l.tasks.some(t=>t.startsWith("URGENT"))?C.redBg:C.orangeBg}/>:<Badge label="Clear" color={C.green} bg={C.greenBg}/>}</td>
        </tr>;})}</tbody>
      </table></div>
    </div>
  </div>);
}

// ═══════ AUTOMATION HUB ═══════
function AutoHub({loans,sel,setSel,to,setTo}){
  const [fd,sFd]=useState({});
  const pl=loans.filter(l=>l.stage!=="Funded"&&l.stage!=="Closed");
  // sel can be just {id} (a pitch opens the deal by id — App openPitchedFile),
  // so the file is read live out of `loans`; a row never seen falls back to
  // the first open file instead of rendering a stub without docs/followUps.
  const a=(sel&&(loans.find(l=>l.id===sel.id)||(sel.docs?sel:null)))||pl[0];
  const aDocs=(a&&a.docs)||{},aFu=(a&&a.followUps)||{};
  const ts=[
    {id:"fhaCase",t:"FHA Case Number",c:C.gold,d:"Auto-pull 1009 (Uniform Residential Loan Application) and Certification & Authorization from the application package for FHA case number assignment",fs:["Recipient Email","FHA Sponsor ID","Notes / Special Instructions"],af:{"Borrower":a?.b,"Property":a?.prop,"Loan Type":a?.type,"Co-Borrower":"From application"},at:["Form 1009 / Uniform Residential Loan Application","Certification & Authorization"],am:"Generates packet • Editable recipient • Ready to submit for case number"},
    {id:"hoa",t:"HOA Payment Verification",c:C.accent,d:"Auto-generate 2-year history request — 3-day auto follow-up",fs:["HOA Email","HOA Company"],af:{"Borrower":a?.b,"Auto-CC":"You (admin)"},at:["Borrower Auth"],am:"Sends immediately • Auto follow-up Day 3 • Escalation Day 5"},
    {id:"payoff",t:"Payoff Request",c:C.purple,d:"Auto-fill borrower & loan # from file — enter good-through date",fs:["Good Through Date","Servicer Email"],af:{"Borrower":a?.b,"Loan #":"Auto from statement"},at:["Borrower Auth"],am:"One-click send • Pre-filled from file"},
    {id:"ins",t:"Insurance Verification",c:C.green,d:"4-point check: balance, coverage, policy copy, lapse confirmation",fs:["Agent Email","Agent Name"],af:{"Borrower":a?.b,"Auto-CC":"You (admin)"},at:["Insurance Doc","Borrower Auth"],am:"Sends to agent • Auto follow-up Day 3 • CC on all"},
  ];

  return(<div><Hdr t="Automation Hub" s="One-click templates with auto follow-up sequences"/>
    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20,flexWrap:"wrap"}}>
      <span style={{fontSize:12,color:C.textDim,fontWeight:700}}>FILE:</span>
      <Sel value={a?.id||""} onChange={e=>setSel(loans.find(l=>l.id===e.target.value))} options={pl.map(l=>({value:l.id,label:`${l.id} — ${l.b}`}))}/>
      {a&&<Badge label={a.stage} color={STC[a.stage]} bg={`${STC[a.stage]}18`}/>}
      {a?.condo&&<Badge label={`FHA: ${a.fha||"N/A"}`} color={a.fha==="Approved"?C.green:a.fha==="Expired"?C.orange:C.textDim} bg={a.fha==="Approved"?C.greenBg:a.fha==="Expired"?C.orangeBg:`${C.textDim}18`}/>}
    </div>

    {a&&<div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
      {[{l:"HOA",s:aDocs.hoa,fu:aFu.hoa},{l:"Payoff",s:aDocs.payoff,fu:aFu.payoff},{l:"Insurance",s:aDocs.ins,fu:aFu.ins},{l:"App",s:aDocs.app}].map(x=><div key={x.l} style={{padding:"8px 14px",borderRadius:8,background:C.surfaceAlt,border:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:6}}>
        <span style={{fontSize:12,color:C.textDim}}>{x.l}:</span>
        <Badge label={x.s==="received"||x.s==="clear"?"Done":x.s==="sent"?"Sent":x.s==="pending"?"Pending":x.s==="flagged"?"Flag":"—"} color={x.s==="received"||x.s==="clear"?C.green:x.s==="sent"?C.accent:x.s==="pending"?C.orange:x.s==="flagged"?C.red:C.textDim} bg={x.s==="received"||x.s==="clear"?C.greenBg:x.s==="sent"?C.accentGlow:x.s==="pending"?C.orangeBg:x.s==="flagged"?C.redBg:`${C.textDim}15`}/>
        {x.fu&&x.fu>=3&&<Badge label={`${x.fu}d!`} color={C.red} bg={C.redBg}/>}
      </div>)}
    </div>}

    {/* Automation Sequence Visualization */}
    <div style={{background:C.surfaceAlt,borderRadius:10,border:`1px solid ${C.border}`,padding:"14px 20px",marginBottom:22}}>
      <div style={{fontSize:11,fontWeight:700,color:C.textDim,textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>Auto Follow-Up Sequence</div>
      <div style={{display:"flex",alignItems:"center",gap:0}}>
        {[{l:"Day 0",d:"Request Sent",c:C.accent},{l:"Day 3",d:"Auto Follow-Up",c:C.orange},{l:"Day 5+",d:"Urgent Escalation",c:C.red}].map((s,i)=><React.Fragment key={i}>
          {i>0&&<div style={{width:40,height:2,background:C.border}}/>}
          <div style={{textAlign:"center",minWidth:100}}>
            <div style={{width:32,height:32,borderRadius:"50%",background:`${s.c}20`,border:`2px solid ${s.c}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 6px",color:s.c,fontSize:12,fontWeight:700}}>{i+1}</div>
            <div style={{fontSize:11,fontWeight:700,color:s.c}}>{s.l}</div>
            <div style={{fontSize:10,color:C.textDim}}>{s.d}</div>
          </div>
        </React.Fragment>)}
      </div>
    </div>

    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(330px,1fr))",gap:16}}>
      {ts.map(t=><div key={t.id} style={{background:C.card,borderRadius:12,border:`1px solid ${C.border}`,overflow:"hidden"}}>
        <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`}}><h3 style={{fontSize:15,fontWeight:700,margin:0,color:t.c}}>{t.t}</h3><p style={{fontSize:12,color:C.textMuted,margin:"4px 0 0"}}>{t.d}</p></div>
        {to===t.id?<div style={{padding:18}}>
          <div style={{marginBottom:12}}><div style={{fontSize:10,fontWeight:700,color:C.textDim,marginBottom:6,textTransform:"uppercase"}}>Auto-Populated</div>{Object.entries(t.af).map(([k,v])=><div key={k} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`1px solid ${C.border}`}}><span style={{fontSize:12,color:C.textDim}}>{k}</span><span style={{fontSize:12,fontWeight:500}}>{v}</span></div>)}</div>
          <div style={{marginBottom:12}}><div style={{fontSize:10,fontWeight:700,color:C.textDim,marginBottom:6,textTransform:"uppercase"}}>Fill In</div>{t.fs.map(f=><div key={f} style={{marginBottom:8}}><label style={{fontSize:11,color:C.textDim,display:"block",marginBottom:4}}>{f}</label><Inp placeholder={f} value={fd[`${t.id}-${f}`]||""} onChange={e=>sFd({...fd,[`${t.id}-${f}`]:e.target.value})}/></div>)}</div>
          <div style={{marginBottom:14}}><div style={{fontSize:10,fontWeight:700,color:C.textDim,marginBottom:4,textTransform:"uppercase"}}>Auto-Attached</div>{t.at.map(a=><div key={a} style={{fontSize:12,color:C.green,padding:"3px 0"}}>{"✓"} {a}</div>)}</div>
          <div style={{padding:"8px 10px",background:C.accentGlow,borderRadius:6,fontSize:11,color:C.accentLight,marginBottom:14}}>{"⚡"} {t.am}</div>
          <div style={{display:"flex",gap:8}}><Btn primary onClick={()=>{
            var recipientField=t.fs.find(f=>f.toLowerCase().includes("email"));
            var toEmail=recipientField?(fd[`${t.id}-${recipientField}`]||""):"";
            var subject=t.t+(a?" — "+a.b:"");
            var bodyLines=[t.t+" Request",""];
            Object.entries(t.af).forEach(function(e){bodyLines.push(e[0]+": "+e[1]);});
            bodyLines.push("");
            t.fs.forEach(function(f){var v=fd[`${t.id}-${f}`]||"";if(v&&!f.toLowerCase().includes("email"))bodyLines.push(f+": "+v);});
            bodyLines.push("","Attached: "+t.at.join(", "),"","Thank you,","Reece Andrews","Reverse by Reece | Summit Lending","NMLS #1449852");
            var mailto="mailto:"+encodeURIComponent(toEmail)+"?subject="+encodeURIComponent(subject)+"&body="+encodeURIComponent(bodyLines.join("\n"));
            window.open(mailto,"_blank");
            setTo(null);
          }}>{Ic.send} Send & Start Automation</Btn><Btn onClick={()=>setTo(null)}>Cancel</Btn></div>
        </div>:<div style={{padding:"12px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}><span style={{fontSize:11,color:C.textDim}}>{t.at.length} attached • {t.fs.length} fields • Auto follow-up</span><Btn small primary onClick={()=>setTo(t.id)}>Launch</Btn></div>}
      </div>)}
    </div>
  </div>);
}

export { UrgentAlertBanner, WorkQueues, Dashboard, AutoHub, OwnerChip, claimLoan, ReimbursementsOwedPanel };
